import React, { createContext, useState, useContext, useEffect } from 'react';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  
  // 🌟 Safety token fallback check reads both key aliases so your state never drops on refresh
  const getActiveToken = () => {
    return localStorage.getItem('modische_token') || 
           localStorage.getItem('modische_auth_token') || 
           localStorage.getItem('token');
  };

  // Track authentication state dynamically based on token presence
  const [hasToken, setHasToken] = useState(!!getActiveToken());

  // 🌟 1. FETCH FROM CLOUD ON APPLICATION INITIALIZATION / REFRESH
  useEffect(() => {
    const syncFromDatabaseCloud = async () => {
      const token = getActiveToken();
      if (!token) {
        setCart([]);
        setWishlist([]);
        return;
      }

      try {
        const response = await fetch('https://api.modischekledings.com/api/users/profile', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const userData = await response.json();

        if (response.ok && userData) {
          const restoredCart = (userData.cart || []).map(item => {
            if (item.productId && typeof item.productId === 'object') {
              return {
                ...item.productId,
                _id: item.productId._id,
                size: item.size,
                quantity: item.quantity
              };
            }
            return null;
          }).filter(Boolean);

          setCart(restoredCart);
          setWishlist(userData.wishlist || []);
        }
      } catch (err) {
        console.error("Failed fetching persistent profile rows from database cloud:", err);
      }
    };

    syncFromDatabaseCloud();
  }, [hasToken]); // Fires instantly on hard refresh or right when a user signs in successfully!

  // 🌟 2. PUSH BULK CHANGES BACK TO MONGODB ATLAS AUTOMATICALLY
  useEffect(() => {
    const token = getActiveToken();
    if (!token) return;

    // Trigger update request whenever states are mutated by actions below
    const syncToDatabaseCloud = async () => {
      try {
        await fetch('https://api.modischekledings.com/api/users/sync', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ cart, wishlist })
        });
      } catch (err) {
        console.error("Cloud database synchronization request failed:", err);
      }
    };

    // Use a tiny 300ms debounce buffer to bundle multi-clicks gracefully
    const scheduleSync = setTimeout(() => {
      syncToDatabaseCloud();
    }, 300);

    return () => clearTimeout(scheduleSync);
  }, [cart, wishlist]);

  // 🛍️ CART ACTIONS WITH AUTHENTICATION GUARDS
  const addToCart = (product, selectedSize) => {
    const token = getActiveToken();
    if (!token) {
      alert("Please sign in to add items to your shopping bag.");
      return false;
    }

    if (!selectedSize) {
      alert("Please select a size before adding to bag.");
      return false;
    }

    setCart((prevCart) => {
      const targetId = product._id || product.id;
      const existingIndex = prevCart.findIndex(
        (item) => (item._id === targetId || item.id === targetId) && item.size === selectedSize
      );

      if (existingIndex > -1) {
        const newCart = prevCart.map((item, idx) => 
          idx === existingIndex ? { ...item, quantity: item.quantity + 1 } : item
        );
        return newCart;
      } else {
        return [...prevCart, { ...product, _id: targetId, size: selectedSize, quantity: 1 }];
      }
    });
    return true;
  };

  const decreaseQuantity = (productId, selectedSize) => {
    setCart((prevCart) => {
      const existingIndex = prevCart.findIndex(
        (item) => (item._id === productId || item.id === productId) && item.size === selectedSize
      );

      if (existingIndex > -1) {
        if (prevCart[existingIndex].quantity > 1) {
          return prevCart.map((item, idx) => 
            idx === existingIndex ? { ...item, quantity: item.quantity - 1 } : item
          );
        } else {
          return prevCart.filter((_, idx) => idx !== existingIndex);
        }
      }
      return prevCart;
    });
  };

  const removeFromCart = (productId, size) => {
    setCart((prevCart) => prevCart.filter((item) => !((item._id === productId || item.id === productId) && item.size === size)));
  };

  const clearCart = () => setCart([]);

  const toggleWishlist = (product) => {
    const token = getActiveToken();
    if (!token) {
      alert("Please sign in to save items to your wishlist.");
      return false;
    }

    setWishlist((prevWishlist) => {
      const targetId = product._id || product.id;
      const exists = prevWishlist.find((item) => (item._id === targetId || item.id === targetId));
      if (exists) {
        return prevWishlist.filter((item) => !(item._id === targetId || item.id === targetId));
      } else {
        return [...prevWishlist, { ...product, _id: targetId }];
      }
    });
    return true;
  };

  const getCartCount = () => cart.reduce((total, item) => total + (item.quantity || 1), 0);

  // Expose token update trigger so login forms can re-trigger hydration instantly on sign-in
  const triggerAuthSync = () => setHasToken(!!getActiveToken());

  return (
    <CartContext.Provider value={{ 
      cart, wishlist, addToCart, decreaseQuantity, removeFromCart, clearCart, toggleWishlist, getCartCount, triggerAuthSync, setCart, setWishlist
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);